import React from 'react';
import { Tabs } from '../ui';
import { SignUpForm } from './SignUpForm';
import { LoginForm } from './LoginForm';
import SSOSection from './SSOSection';
import { SignUpFormData, LoginFormData } from '../../types/auth';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { API_URLS } from '../../constants';
import { useMicrosoftAuth } from '../../hooks/useMicrosoftAuth';

const AuthPage = () => {
  // Get the mode from URL parameters
  const { mode } = useParams<{ mode?: string }>();
  const navigate = useNavigate();
  const { loginWithCredentials } = useAuth();
  const { login: microsoftLogin, loading: microsoftLoading, error: microsoftError } = useMicrosoftAuth();
  
  // Set initial tab based on URL or default to login
  const [activeTab, setActiveTab] = React.useState(mode === 'signup' ? 'signup' : 'login');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({});
  const [loginSuccess, setLoginSuccess] = React.useState(false);
  const [signUpLoadingMessage, setSignUpLoadingMessage] = React.useState<string | null>(null);
  const [signUpSuccess, setSignUpSuccess] = React.useState(false);
  const [loginLoadingMessage, setLoginLoadingMessage] = React.useState<string | null>(null);

  const handleLogin = async (data: LoginFormData) => {
    setLoading(true);
    setError(null);
    setFieldErrors({});
    setLoginLoadingMessage('Signing you in...');
    
    try {
  
      const result = await loginWithCredentials(data.email, data.password);
      
      if (result.success) {
  
        setLoginLoadingMessage('Login successful! Redirecting...');
        setError(null);
        setFieldErrors({});
        setLoginSuccess(true);
        
        // Redirect after showing success message
        setTimeout(() => {
          setLoginSuccess(false);
          setLoginLoadingMessage(null);
        }, 1500);
      } else {
  
        setLoginLoadingMessage(null);
        
        // Enhanced error handling with specific messages
        if (result.error?.includes('Invalid email or password') || result.error?.includes('Invalid credentials')) {
          setFieldErrors({ password: 'The email or password you entered is incorrect. Please try again.' });
          setError(null);
        } else if (result.error?.includes('Network error') || result.error?.includes('fetch')) {
          setError('Unable to connect to the server. Please check your internet connection and try again.');
          setFieldErrors({});
        } else if (result.error?.includes('timeout')) {
          setError('The request timed out. Please try again.');
          setFieldErrors({});
        } else {
          setError(result.error || 'An unexpected error occurred. Please try again.');
          setFieldErrors({});
        }
      }
    } catch (error) {
  
      setLoginLoadingMessage(null);
      setError('A network error occurred. Please check your connection and try again.');
      setFieldErrors({});
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (data: SignUpFormData) => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    setFieldErrors({});
    setSignUpLoadingMessage('Creating your account...');
    
    try {
      const response = await fetch(API_URLS.AUTH.REGISTER, {
        method: 'POST',
        credentials: 'include', // Include cookies for automatic login after registration
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          first_name: data.firstName,
          last_name: data.lastName,
          email: data.email,
          password: data.password,
          confirmPassword: data.confirmPassword,
          function: data.function,
        }),
      });

      const result = await response.json();
      
      if (!response.ok) {
        setSignUpLoadingMessage(null);
        
        if (response.status === 400 && result.errors) {
          // Field-level errors from backend
          const errors: Record<string, string> = {};
          result.errors.forEach((err: any) => {
            let field = err.path?.[0] || err.field;
            if (field === 'first_name') field = 'firstName';
            if (field === 'last_name') field = 'lastName';
            if (field === 'function') field = 'function';
            errors[field] = err.message;
          });
          setFieldErrors(errors);
          setError(null);
        } else if (response.status === 409) {
          setFieldErrors({ email: 'This email is already registered. Please try logging in instead.' });
          setError(null);
        } else if (response.status >= 500) {
          setError('Server error occurred. Please try again later.');
          setFieldErrors({});
        } else {
          setError(result.message || 'Registration failed. Please try again.');
          setFieldErrors({});
        }
        return;
      }
      
      // Registration successful - redirect to email verification

      setSignUpLoadingMessage('Account created! Please check your email for verification...');
      
      setTimeout(() => {
        setSignUpSuccess(true);
        setError(null);
        setFieldErrors({});
        setSignUpLoadingMessage('Redirecting to email verification...');
        
        // Redirect to email verification page
        setTimeout(() => {
          setSignUpSuccess(false);
          setSignUpLoadingMessage(null);
          navigate(`/auth/verify-email?email=${encodeURIComponent(data.email)}`);
        }, 1500);
      }, 800);
      
    } catch (err: any) {
  
      setSignUpLoadingMessage(null);
      
      if (err?.message?.includes('fetch') || err?.message?.includes('Network')) {
        setError('Unable to connect to the server. Please check your internet connection and try again.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setFieldErrors({});
    } finally {
      setLoading(false);
    }
  };

  // Handle tab switching with URL updates
  const switchToLogin = () => {
    setActiveTab('login');
    if (navigate) navigate('/auth/login');
  };
  
  const switchToSignUp = () => {
    setActiveTab('signup');
    if (navigate) navigate('/auth/signup');
  };

  const tabs = [
    {
      id: 'signup',
      label: 'Sign Up',
      content: (
        <div className="space-y-4">
          <SignUpForm
            onSubmit={handleSignUp}
            loading={loading}
            onSwitchToLogin={switchToLogin}
            fieldErrors={fieldErrors}
            generalError={error}
            feedbackBelowButton={
              signUpLoadingMessage || signUpSuccess ? (
                <>
                  {signUpLoadingMessage && (
                    <div className="bg-brand-light-gray border border-gray-200 rounded-md text-brand-electric-blue text-sm text-center mt-4">
                      {signUpLoadingMessage}
                    </div>
                  )}
                  {signUpSuccess && (
                    <div className="bg-green-100 border border-green-300 rounded-md text-green-700 text-sm text-center flex items-center justify-center gap-2 mt-4">
                      <svg className="h-5 w-5 text-green-500 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {success}
                    </div>
                  )}
                </>
              ) : null
            }
          />
          <SSOSection disabled={loading} mode="signup" />
        </div>
      ),
    },
    {
      id: 'login',
      label: 'Login',
      content: (
        <div className="space-y-4">
          <LoginForm onSubmit={handleLogin} loading={loading} onSwitchToSignUp={switchToSignUp} fieldErrors={fieldErrors} generalError={error} />
          
          <SSOSection disabled={loading} mode="login" />
          
          <button
            onClick={async () => {
              const result = await microsoftLogin();
              if (result.success) {
                navigate('/');
              }
            }}
            disabled={microsoftLoading || loading}
            className="w-full bg-brand-dark-blue hover:bg-brand-electric-blue disabled:bg-gray-400 text-white font-medium py-3 px-4 rounded-md transition-colors duration-200 flex items-center justify-center gap-2"
          >
            {microsoftLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Signing in with Microsoft...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M11.4 24H0V12.6h11.4V24zM11.4 11.4H0V0h11.4v11.4zM24 11.4H12.6V0H24v11.4zM24 24H12.6V12.6H24V24z"/>
                </svg>
                Sign in with Microsoft
              </>
            )}
          </button>
          
          {microsoftError && (
            <div className="mt-2 text-red-600 text-sm text-center">
              {microsoftError}
            </div>
          )}
          {loginSuccess && (
            <div className="bg-green-100 text-green-700 border border-green-300 rounded-md p-3 mt-4 text-center text-sm flex items-center justify-center gap-2">
              <svg className="h-5 w-5 text-green-500 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Login successful! Redirecting...
            </div>
          )}
          {loginLoadingMessage && (
            <div className="bg-brand-light-gray border border-gray-200 rounded-md text-brand-electric-blue text-sm text-center mt-4">
              {loginLoadingMessage}
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <main className="min-h-screen bg-gray-50 flex items-center justify-center py-8 px-4">
      <div className="max-w-md w-full bg-white p-6 rounded-lg shadow-sm">
        <div className="text-center mb-4">
          <h1 className="text-2xl font-bold text-gray-900">Welcome to Prompt Shelf!</h1>
          <p className="mt-1 text-gray-600">Where prompts become playbooks</p>
          <p className="text-sm text-gray-500 mt-1">Curated prompts. Shared knowledge. Smarter teams.</p>
        </div>
        
        <Tabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={(tab) => {
            setActiveTab(tab);
            if (navigate) navigate(tab === 'login' ? '/auth/login' : '/auth/signup');
          }}
        />
        
        {/* Terms of Service - Only at the bottom */}
        <div className="mt-8 pt-4 border-t border-gray-100">
          <p className="text-center text-xs text-gray-500">
            By signing up for Prompt Shelf, you agree to our{' '}
            <a href="https://www.promptshelf.ai/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-brand-electric-blue hover:text-brand-dark-blue underline">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="https://www.promptshelf.ai/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-brand-electric-blue hover:text-brand-dark-blue underline">
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
    </main>
  );
};

export { AuthPage };